import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@avila-roffe/components-web';
import { getAllServices } from '../../api/healthApi';
import { useHealth, useHealthInfo } from '../../hooks/useHealth';

// ── Service label map ─────────────────────────────────────────────────────────

const SERVICE_LABELS: Record<string, string> = {
  database: 'PostgreSQL',
  redis: 'Redis',
  litellm: 'LiteLLM',
  'chat-manager': 'Chat Manager',
};

function serviceLabel(key: string): string {
  return SERVICE_LABELS[key] ?? key;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

function formatMemory(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

// ── StatusDot ─────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: 'up' | 'down' | 'unknown' }) {
  const colors = {
    up: 'bg-emerald-500',
    down: 'bg-destructive',
    unknown: 'bg-muted-foreground',
  };
  return <span className={`inline-block size-2 rounded-full ${colors[status]}`} />;
}

// ── HealthDialog ──────────────────────────────────────────────────────────────

interface HealthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HealthDialog({ open, onOpenChange }: HealthDialogProps) {
  const { data: health, isLoading: healthLoading } = useHealth();
  const { data: info, isLoading: infoLoading } = useHealthInfo();

  const services = health ? getAllServices(health) : {};
  const overallOk = health?.status === 'ok';

  const frontendVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.1';
  const frontendCommit = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? 'unknown';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            System Status
            {!healthLoading && health && (
              <Badge variant={overallOk ? 'success' : 'destructive'} className="text-xs">
                {overallOk ? 'All systems operational' : 'Degraded'}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Dependency health and version information for this deployment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* ── Service statuses ── */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Dependencies
            </h3>
            {healthLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : !health ? (
              <p className="text-xs text-muted-foreground">
                Unable to reach araf-factory — backend may be offline.
              </p>
            ) : (
              <div className="rounded-md border divide-y">
                {Object.entries(services).map(([key, svc]) => (
                  <div key={key} className="px-3 py-2 space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{serviceLabel(key)}</span>
                      <div className="flex items-center gap-1.5">
                        <StatusDot status={svc.status} />
                        <span
                          className={`text-xs font-medium ${svc.status === 'up' ? 'text-emerald-600' : 'text-destructive'}`}
                        >
                          {svc.status}
                        </span>
                      </div>
                    </div>
                    {svc.url && (
                      <code className="block text-xs text-muted-foreground font-mono truncate">
                        {svc.url}
                      </code>
                    )}
                    {svc.message && (
                      <p className="text-xs text-destructive truncate">{svc.message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Version info ── */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Version
            </h3>
            <div className="rounded-md border divide-y text-sm">
              {/* Frontend */}
              <div className="px-3 py-2 flex items-center justify-between">
                <span className="text-muted-foreground">Frontend</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{frontendVersion}</span>
                  <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
                    {frontendCommit.slice(0, 8)}
                  </code>
                </div>
              </div>

              {/* Backend */}
              {infoLoading ? (
                <div className="px-3 py-2">
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : info ? (
                <>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">Backend</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{info.version}</span>
                      <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
                        {info.commit.slice(0, 8)}
                      </code>
                    </div>
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">Uptime</span>
                    <span>{formatUptime(info.uptime)}</span>
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">Memory</span>
                    <span>
                      {formatMemory(info.memory.heapUsed)} / {formatMemory(info.memory.heapTotal)}
                    </span>
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">Node.js</span>
                    <span>{info.node}</span>
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">Environment</span>
                    <Badge variant="secondary" className="text-xs">
                      {info.environment}
                    </Badge>
                  </div>
                </>
              ) : null}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
